import { useEffect, useMemo, useState } from 'react'
import camelCase from 'lodash/camelCase'
import startCase from 'lodash/startCase'
import { useAuth0 } from '@auth0/auth0-react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { APP_UPDATE_NOTICE, SnackbarContext } from '../../../providers/SnackbarContext'
import { AppStateContext } from '../../../providers/AppStateContext'
import { generatePermalink } from '../../../utils/formatting'

import { TrailSystem } from '../../../models/TrailSystem.model'
import { TrailSystemAddEvent } from '../../../events/TrailSystemAddEvent'
import { TrailSystemUpdateEvent } from '../../../events/TrailSystemUpdateEvent'
import { AppPermissions } from '../../../models/AppPermissions.model'
import { USStates } from '../../../models/USStates.model'

import UserService from '../../../services/UserService/UserService'
import { WeatherGroupService } from '../../../services/WeatherGroupService/WeatherGroupService'
import { TrailSystemService } from '../../../services/TrailSystemService/TrailSystemService'

import {
  Stack,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormHelperText,
  Button,
  Select,
  MenuItem,
  InputAdornment,
  ListItemIcon,
  Checkbox,
  SelectChangeEvent,
  ListItemText,
  Divider,
  Typography,
  Box,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import Loading from '../../../components/Loading/Loading.component'
import DetailInput from '../../../components/DetailInput/DetailInput.component'
import { TrailSystemRelations } from '../../../models/TrailSystemRelations.model'
import { useAppNavigate } from '../../../utils/url'
import { TrailStewardService } from '../../../services/TrailStewardService/TrailStewardService'
import GoogleMapsFetchDialog from '../../../components/GoogleMapsFetchDialog/GoogleMapsFetchDialog.component'
import { ParkingLot } from '../../../models/ParkingLot.model'
import SectionedForm, { FormSection } from '../../../components/Sections/SectionedForm.component'
import SectionSubheading from '../../../components/Sections/SectionSubheading.component'
import WeatherGroupPriorityList from '../../../components/WeatherGroupPriorityList/WeatherGroupPriorityList.component'
import { TrailSystemSectionIds, trailSystemSections } from './TrailSystemSections'
import { sortResponseByName } from '../../../utils/sorting'

export type TrailSystemFormProps = {
  trailSystem?: TrailSystem
  onAdd?: (trailSystem: TrailSystem) => void
  onEdit?: (trailSystem: TrailSystem) => void
  onCancel: () => void
  // used to remotely trigger the save action
  saveTrigger?: number
  disableRedirect?: boolean
  // section of the form to scroll to when it opens (see TrailSystemSectionIds)
  initialSection?: string
}
const TrailSystemForm = (props: TrailSystemFormProps) => {
  const { trailSystem, onCancel, onAdd, onEdit, saveTrigger, disableRedirect = false, initialSection } = props
  const navigate = useAppNavigate()

  const { user } = useAuth0()
  const formattedUser = useMemo(() => (user ? UserService.formatUser(user) : undefined), [user])
  const hasViewWeatherGroupPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherGroup])
  const isAdding = typeof onAdd === 'function'
  const isEditing = typeof onEdit === 'function'

  // make sure steward is an array of number
  if (trailSystem?.trailStewards && Array.isArray(trailSystem.trailStewards)) {
    // If the first item in the array is an object with an 'id' field, convert to an array of numbers
    if (
      trailSystem.trailStewards.length > 0 &&
      typeof trailSystem.trailStewards[0] === 'object' &&
      'id' in trailSystem.trailStewards[0]
    ) {
      // Modify the array in-place to convert objects to numbers
      trailSystem.trailStewards = trailSystem.trailStewards.map((steward: number | { id: number }) =>
        typeof steward === 'object' ? steward.id : steward,
      )
    }
  }
  // Initialize with at least one parking lot
  const [tempTrailSystem, setTempTrailSystem] = useState<
    Partial<TrailSystem> & {
      parkingLots?: Partial<ParkingLot>[]
      weatherGroupsWithPriority?: { weatherGroupId: number; priority: number }[] | null
      displayWeatherGroupId?: number
    }
  >({
    ...trailSystem,
    name: trailSystem?.name || '',
    street: trailSystem?.street || '',
    city: trailSystem?.city || '',
    state: trailSystem?.state || undefined,
    permalink: trailSystem?.permalink || '',
    description: trailSystem?.description || '',
    sortWeight: trailSystem?.sortWeight || 99,
    active: trailSystem?.active ?? true,
    precipitationLookbackDays: trailSystem?.precipitationLookbackDays || undefined,
    freezeThawEnabled: trailSystem?.freezeThawEnabled ?? undefined,
    trailforksSyncEnabled: trailSystem?.trailforksSyncEnabled ?? false,
    trailforksRegionId: trailSystem?.trailforksRegionId ?? '',
    saturationModifier: trailSystem?.saturationModifier ?? undefined,
    trailStewards: trailSystem?.trailStewards || [],
    parkingLots: trailSystem?.parkingLots?.length
      ? trailSystem.parkingLots.map((lot) => ({
          name: lot.name || '',
          gpsLat: lot.gpsLat || 0,
          gpsLong: lot.gpsLong || 0,
        }))
      : [],
    weatherGroupsWithPriority: trailSystem?.weatherGroups
      ? trailSystem.weatherGroups.map((wg) => ({
          weatherGroupId: wg.id,
          priority: wg.priority,
        }))
      : [],
    displayWeatherGroupId: trailSystem?.displayWeatherGroup?.id || undefined,
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isGoogleMapsDialogOpen, setIsGoogleMapsDialogOpen] = useState(false)
  const [parkingDialogIndex, setParkingDialogIndex] = useState<number | null>(null)
  const handleChange = (event: SelectChangeEvent<typeof tempTrailSystem.trailStewards>) => {
    const value = event.target.value as number[]
    if (value[value.length - 1] === -2) {
      const allTrails = trailStewardResponse?.data.map((option) => option.id)
      setTempTrailSystem((prev) => ({
        ...prev,
        trailStewards: prev.trailStewards?.length === trailStewardResponse?.data.length ? [] : allTrails ?? [],
      }))
      return
    }
    setTempTrailSystem((prev) => ({
      ...prev,
      trailStewards: value,
    }))
  }

  const queryClient = useQueryClient()

  // if they have hasViewWeatherGroupPermissions and the trail system passed does not have weather group relations, fetch the trail system with the relations
  const { isLoading: isLoadingTrailSystemWithWeatherGroups, data: trailSystemWithWeatherGroups } = useQuery(
    [
      'TrailSystemService.get',
      { id: trailSystem?.id, relations: [TrailSystemRelations.weatherGroups, TrailSystemRelations.trailStewards] },
      AppStateContext.getRegion(),
    ],
    () => {
      const relations = [TrailSystemRelations.weatherGroups, TrailSystemRelations.trailStewards]
      return trailSystemService.get({
        id: trailSystem?.id,
        relations: relations,
      })
    },
    {
      enabled: !!(hasViewWeatherGroupPermissions && trailSystem?.id && !trailSystem?.weatherGroups?.length),
      // prevent the query from running unless they have the permissions and the trail system does not have weather group relations
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch weather group assignments for the trail system: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )
  // useEffect usage instead of onSuccess or onSettled to ensure we always trigger even when the query is cached
  useEffect(() => {
    if (trailSystemWithWeatherGroups) {
      setTempTrailSystem((prev) => ({
        ...prev,
        weatherGroupsWithPriority: (trailSystemWithWeatherGroups.weatherGroups ?? []).map((each) => ({
          weatherGroupId: each.id,
          priority: each.priority,
        })),
        displayWeatherGroup: trailSystemWithWeatherGroups?.displayWeatherGroup,
        displayWeatherGroupId: trailSystemWithWeatherGroups?.displayWeatherGroup?.id,
      }))
    }
  }, [trailSystemWithWeatherGroups])

  // For adding a parking lot to the list of parking lots
  const addParkingLot = () => {
    setTempTrailSystem((prev) => {
      const existingLots = prev.parkingLots ?? []
      return {
        ...prev,
        parkingLots: [
          ...existingLots,
          {
            name: existingLots.length === 0 ? 'Main Parking' : '',
            gpsLat: 0,
            gpsLong: 0,
          },
        ],
      }
    })
  }

  const removeParkingLot = (index: number) => {
    setTempTrailSystem((prev) => ({
      ...prev,
      parkingLots: prev.parkingLots?.filter((_, i) => i !== index) || [],
    }))
  }

  const updateParkingLot = <K extends keyof ParkingLot>(index: number, field: K, value: ParkingLot[K]) => {
    setTempTrailSystem((prev) => ({
      ...prev,
      parkingLots: prev.parkingLots?.map((lot, i) => (i === index ? { ...lot, [field]: value } : lot)) || [],
    }))
  }

  // if they have hasViewWeatherGroupPermissions, fetch the list of weather groups for them to select from
  const weatherGroupService = useMemo(() => new WeatherGroupService(), []) // memo to prevent re-creating service on every render
  const { isLoading: isLoadingWeatherGroups, data: weatherGroupsResponse } = useQuery(
    ['WeatherGroupService.search', { pageSize: 0 }, AppStateContext.getRegion()],
    () => weatherGroupService.search({ pageSize: 0 }),
    {
      enabled: hasViewWeatherGroupPermissions, // prevent the query from running unless they have the permissions
      // list options alphabetically
      select: sortResponseByName,
      onError: (err) => {
        SnackbarContext.show(
          `Failed to fetch weather groups for the form: ${typeof err === 'string' ? err : ''}`,
          'error',
        )
        console.error(err)
      },
    },
  )

  const trailSystemService = useMemo(() => new TrailSystemService(), []) // memo to prevent re-creating service on every render

  const addTrailSystem = useMutation(
    (mutationParams: { trailSystem: TrailSystemAddEvent }) => {
      setIsSubmitting(true)
      return trailSystemService.add(mutationParams.trailSystem)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Trail System added successfully!', 'success', APP_UPDATE_NOTICE)

        if (!disableRedirect) {
          // redirect to the new trail system
          navigate(`/trail-systems/${result.id}`)
          // remove the search query to prevent auto refresh of the search results in case we are on that page and redirecting
          queryClient.removeQueries(['TrailSystemService.search'])
        }
        // invalidate all other queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onAdd === 'function') {
          onAdd(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Trail System failed to add: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const updateTrailSystem = useMutation(
    (mutationParams: { id: number; changedProperties: TrailSystemUpdateEvent }) => {
      setIsSubmitting(true)
      return trailSystemService.update(mutationParams.id, mutationParams.changedProperties)
    },
    {
      onSuccess: (result) => {
        SnackbarContext.show('Trail System updated successfully!', 'success', APP_UPDATE_NOTICE)

        // invalidate all queries to ensure any queries that are using the modified record are updated
        queryClient.invalidateQueries()
        setIsSubmitting(false)

        if (typeof onEdit === 'function') {
          onEdit(result)
        }
      },
      onError: (err) => {
        SnackbarContext.show(`Trail System failed to update: ${err}`, 'error')
        console.error(err)
        setIsSubmitting(false)
      },
    },
  )

  const trailStewardService = useMemo(() => new TrailStewardService(), [])
  const { isLoading: isLoadingTrailStewards, data: trailStewardResponse } = useQuery(
    [
      'TrailStewardService.search',
      {
        systemId: trailSystem?.id,
      },
      AppStateContext.getRegion(),
    ],
    () =>
      trailStewardService.search({
        page: 1,
        pageSize: 200,
        orderBy: 'createdAt',
        orderDirection: 'DESC',
      }),
    {
      // list options alphabetically
      select: sortResponseByName,
      onError: (err) => {
        SnackbarContext.show(typeof err === 'string' ? err : 'An error occurred while searching for sponsors.', 'error')
        console.error(err)
      },
    },
  )

  const isAllSelected =
    trailStewardResponse !== undefined &&
    trailStewardResponse?.data.length > 0 &&
    tempTrailSystem.trailStewards?.length === trailStewardResponse?.data.length

  // Weather group handlers
  const handleWeatherGroupChange = (idx: number, field: 'weatherGroupId' | 'priority', value: number) => {
    setTempTrailSystem((prev) => {
      const arr = prev.weatherGroupsWithPriority ? [...prev.weatherGroupsWithPriority] : []
      arr[idx] = { ...arr[idx], [field]: value }
      return { ...prev, weatherGroupsWithPriority: arr }
    })
  }

  const handleAddWeatherGroup = () => {
    const available = weatherGroupsResponse?.data?.find(
      (wg) => !tempTrailSystem.weatherGroupsWithPriority?.some((w) => w.weatherGroupId === wg.id),
    )
    if (!available) return
    setTempTrailSystem((prev) => {
      const arr = [...(prev.weatherGroupsWithPriority ?? []), { weatherGroupId: available.id, priority: 0 }]
      const updated = arr.map((item, index) => ({
        ...item,
        priority: index + 1,
      }))
      return { ...prev, weatherGroupsWithPriority: updated }
    })
  }

  const handleRemoveWeatherGroup = (idx: number) => {
    setTempTrailSystem((prev) => {
      const arr = [...(prev.weatherGroupsWithPriority ?? [])]
      arr.splice(idx, 1)
      const updated = arr.map((item, index) => ({
        ...item,
        priority: index + 1,
      }))
      return { ...prev, weatherGroupsWithPriority: updated }
    })
  }

  const handleReorderWeatherGroups = (fromIndex: number, toIndex: number) => {
    setTempTrailSystem((prev) => {
      const arr = [...(prev.weatherGroupsWithPriority ?? [])]
      const [moved] = arr.splice(fromIndex, 1)
      arr.splice(toIndex, 0, moved)
      // 🔁 Reassign priorities based on order
      const updated = arr.map((item, index) => ({
        ...item,
        priority: index + 1,
      }))
      return { ...prev, weatherGroupsWithPriority: updated }
    })
  }

  // F7 — Trailforks Region ID. Backend guard refuses to enable trailforksSyncEnabled without at least one
  // populated numeric id. Field accepts comma-separated ids for the rare system that spans multiple TF regions.
  // Client-side validation surfaces the same rule before the request; the backend guard remains the source of truth.
  const trailforksRegionParts = (tempTrailSystem.trailforksRegionId ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
  const invalidTrailforksRegionPart = trailforksRegionParts.find((part) => !/^\d+$/.test(part))
  const canEnableTrailforksSync = trailforksRegionParts.length > 0 && !invalidTrailforksRegionPart
  // If they clear the region after previously enabling sync, auto-disable sync client-side
  // (the backend enforces the same rule on submit).
  useEffect(() => {
    if (!canEnableTrailforksSync && tempTrailSystem.trailforksSyncEnabled) {
      setTempTrailSystem((prev) => (prev.trailforksSyncEnabled ? { ...prev, trailforksSyncEnabled: false } : prev))
    }
  }, [canEnableTrailforksSync, tempTrailSystem.trailforksSyncEnabled])

  const handleSubmit = () => {
    if (!tempTrailSystem.name) {
      SnackbarContext.show(`Trail System name is required`, 'error')
      return
    }
    if (!tempTrailSystem.street) {
      SnackbarContext.show(`Trail System street is required`, 'error')
      return
    }
    if (!tempTrailSystem.city) {
      SnackbarContext.show(`Trail System city is required`, 'error')
      return
    }
    if (!tempTrailSystem.state) {
      SnackbarContext.show(`Trail System state is required`, 'error')
      return
    }
    if (!tempTrailSystem.weatherGroupsWithPriority || tempTrailSystem.weatherGroupsWithPriority.length === 0) {
      SnackbarContext.show(`At least one Weather Group is required`, 'error')
      return
    }
    if (!tempTrailSystem.displayWeatherGroupId) {
      SnackbarContext.show(`Trail System display weather group is required`, 'error')
      return
    }
    if (!trailSystem) {
      // format the data to match the API
      const formattedTrailSystem: TrailSystemAddEvent = {
        name: tempTrailSystem.name,
        permalink: tempTrailSystem.permalink || undefined,
        description: tempTrailSystem.description || undefined,
        street: tempTrailSystem.street,
        city: tempTrailSystem.city,
        state: tempTrailSystem.state,
        parkingLots: tempTrailSystem.parkingLots || [],
        weatherGroupsWithPriority: tempTrailSystem.weatherGroupsWithPriority,
        displayWeatherGroupId: tempTrailSystem.displayWeatherGroupId,
        sortWeight: tempTrailSystem.sortWeight,
        active: tempTrailSystem.active,
        trailStewards: tempTrailSystem.trailStewards,
        precipitationLookbackDays: tempTrailSystem.precipitationLookbackDays,
        freezeThawEnabled: tempTrailSystem.freezeThawEnabled,
        trailforksSyncEnabled: tempTrailSystem.trailforksSyncEnabled ?? false,
        trailforksRegionId: tempTrailSystem.trailforksRegionId?.trim() || undefined,
        dryTimeModifier:
          String(tempTrailSystem.dryTimeModifier ?? '') === '' ? undefined : tempTrailSystem.dryTimeModifier,
        saturationModifier:
          String(tempTrailSystem.saturationModifier ?? '') === '' ? undefined : tempTrailSystem.saturationModifier,
      }

      // adding a new trailSystem
      addTrailSystem.mutate({ trailSystem: formattedTrailSystem })
    } else {
      // identify the properties that have changed
      const didChange = (field: keyof TrailSystem) => {
        return tempTrailSystem[field] !== undefined && tempTrailSystem[field] !== trailSystem[field]
      }

      const parkingLotsChanged = () => {
        const original = trailSystem.parkingLots || []
        const current = tempTrailSystem.parkingLots || []

        if (original.length !== current.length) return true

        return original.some((origLot, index) => {
          const currLot = current[index]
          return (
            origLot.name !== currLot.name || origLot.gpsLat !== currLot.gpsLat || origLot.gpsLong !== currLot.gpsLong
          )
        })
      }

      if (!tempTrailSystem.permalink) {
        SnackbarContext.show(`Trail System permalink cannot be blank`, 'error')
        return
      }

      // Compare weatherGroupsWithPriority arrays
      const originalWeatherGroupsWithPriority = trailSystem.weatherGroups
        ? trailSystem.weatherGroups.map((wg) => ({
            weatherGroupId: wg.id,
            priority: wg.priority,
          }))
        : []

      const weatherGroupsChanged =
        JSON.stringify(tempTrailSystem.weatherGroupsWithPriority ?? []) !==
        JSON.stringify(originalWeatherGroupsWithPriority)

      const changedProperties: TrailSystemUpdateEvent = {
        id: trailSystem.id,
        name: didChange('name') ? tempTrailSystem.name : undefined,
        permalink: didChange('permalink') ? tempTrailSystem.permalink : undefined,
        description: didChange('description') ? tempTrailSystem.description : undefined,
        street: didChange('street') ? tempTrailSystem.street : undefined,
        city: didChange('city') ? tempTrailSystem.city : undefined,
        state: didChange('state') ? tempTrailSystem.state : undefined,
        parkingLots: parkingLotsChanged() ? tempTrailSystem.parkingLots : undefined,
        weatherGroupsWithPriority: weatherGroupsChanged ? tempTrailSystem.weatherGroupsWithPriority : undefined,
        displayWeatherGroupId:
          tempTrailSystem.displayWeatherGroupId !== trailSystem.displayWeatherGroup?.id
            ? tempTrailSystem.displayWeatherGroupId
            : undefined,
        sortWeight: didChange('sortWeight') ? tempTrailSystem.sortWeight : undefined,
        active: didChange('active') ? tempTrailSystem.active : undefined,
        trailStewards: didChange('trailStewards') ? tempTrailSystem.trailStewards : undefined,
        precipitationLookbackDays: didChange('precipitationLookbackDays')
          ? String(tempTrailSystem.precipitationLookbackDays) === ''
            ? null
            : tempTrailSystem.precipitationLookbackDays
          : undefined,
        freezeThawEnabled: didChange('freezeThawEnabled') ? tempTrailSystem.freezeThawEnabled : undefined,
        trailforksSyncEnabled: didChange('trailforksSyncEnabled')
          ? tempTrailSystem.trailforksSyncEnabled ?? false
          : undefined,
        trailforksRegionId: didChange('trailforksRegionId')
          ? tempTrailSystem.trailforksRegionId?.trim() || null
          : undefined,
        dryTimeModifier: didChange('dryTimeModifier')
          ? String(tempTrailSystem.dryTimeModifier) === ''
            ? null
            : tempTrailSystem.dryTimeModifier
          : undefined,
        saturationModifier: didChange('saturationModifier')
          ? String(tempTrailSystem.saturationModifier) === ''
            ? null
            : tempTrailSystem.saturationModifier
          : undefined,
      }

      if (!Object.keys(changedProperties).filter((key) => key !== 'id').length) {
        // nothing changed so just close the dialog
        onCancel()
      } else {
        // edit an existing trailSystem
        updateTrailSystem.mutate({ id: trailSystem.id, changedProperties })
      }
    }
  }

  useEffect(() => {
    if (saveTrigger) {
      // if outside consumer triggered save, then submit
      handleSubmit()
    }
  }, [saveTrigger])

  const handleCancel = () => {
    if (typeof onCancel === 'function') {
      onCancel()
    }
  }

  const keyDownSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  return (
    <>
      {(isLoadingTrailSystemWithWeatherGroups || isLoadingWeatherGroups || isSubmitting || isLoadingTrailStewards) && (
        <Loading sx={{ py: 20 }} />
      )}
      {!isLoadingTrailSystemWithWeatherGroups &&
        !isLoadingWeatherGroups &&
        !isSubmitting &&
        !isLoadingTrailStewards && (
          <>
            <SectionedForm sections={trailSystemSections} initialSection={initialSection}>
              {/* --- General ---------------------------------------------------- */}
              <FormSection id={TrailSystemSectionIds.general}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Name</InputLabel>
                  <OutlinedInput
                    id="trailSystem-name"
                    label="Name"
                    value={tempTrailSystem.name || ''}
                    required
                    onChange={(e) => {
                      setTempTrailSystem((prev) => ({ ...prev, name: e.target.value }))
                    }}
                    onKeyDown={keyDownSubmit}
                  />
                </FormControl>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Permalink</InputLabel>
                  <OutlinedInput
                    id="trail-system-permalink"
                    label="Permalink"
                    value={tempTrailSystem.permalink || ''}
                    required={isEditing}
                    startAdornment={
                      // the URL prefix is too wide for phones, which see the full URL below the field instead
                      <InputAdornment position="start" sx={{ display: { zero: 'none', sm: 'flex' } }}>
                        https://{AppStateContext.getRegion().toLowerCase()}.flowfeed.app/trail-system/
                      </InputAdornment>
                    }
                    onChange={(e) => {
                      setTempTrailSystem((prev) => ({ ...prev, permalink: generatePermalink(e.target.value) }))
                    }}
                    onKeyDown={keyDownSubmit}
                  />
                  <FormHelperText>
                    <Box
                      component="span"
                      sx={{
                        display: { zero: 'block', sm: 'none' },
                        color: 'primary.main',
                        overflowWrap: 'anywhere',
                        mb: 0.5,
                      }}
                    >
                      https://{AppStateContext.getRegion().toLowerCase()}.flowfeed.app/trail-system/
                      {tempTrailSystem.permalink}
                    </Box>
                    This is the unique identifier for this trail system used in the URL for this trail system's page
                    within the app. It can only contain letters, numbers, and dashes. Invalid characters will be
                    removed.
                    {isAdding ? ' If you leave this blank, one will be generated for you.' : ''}
                  </FormHelperText>
                </FormControl>
                <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-start' }}>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          id="trail-system-active-switch"
                          checked={tempTrailSystem.active !== false}
                          onChange={(e) => {
                            setTempTrailSystem((prev) => ({ ...prev, active: e.target.checked }))
                          }}
                        />
                      }
                      label={tempTrailSystem.active !== false ? 'Active' : 'Inactive'}
                    />
                    <FormHelperText sx={{ mt: 0 }}>Inactive trail systems are hidden in the app.</FormHelperText>
                  </FormControl>
                  {formattedUser && !formattedUser.trailSystems.length && (
                    <DetailInput<TrailSystem>
                      label="Sort Weight"
                      field="sortWeight"
                      type="number"
                      helperText="Lower numbers will appear first in lists (default 99)"
                      tempDetail={tempTrailSystem}
                      setTempDetail={setTempTrailSystem}
                      keyDownSubmit={keyDownSubmit}
                    />
                  )}
                </Stack>
              </FormSection>

              {/* --- Location --------------------------------------------------- */}
              <FormSection id={TrailSystemSectionIds.location}>
                <SectionSubheading
                  title="Address"
                  action={
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<TravelExploreIcon />}
                      onClick={() => setIsGoogleMapsDialogOpen(true)}
                      sx={{ flexShrink: 0 }}
                    >
                      Fetch from Google Maps
                    </Button>
                  }
                />
                <DetailInput<TrailSystem>
                  label="Street"
                  field="street"
                  tempDetail={tempTrailSystem}
                  setTempDetail={setTempTrailSystem}
                  keyDownSubmit={keyDownSubmit}
                />
                <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5}>
                  <DetailInput<TrailSystem>
                    label="City"
                    field="city"
                    tempDetail={tempTrailSystem}
                    setTempDetail={setTempTrailSystem}
                    keyDownSubmit={keyDownSubmit}
                  />
                  <FormControl variant="outlined" fullWidth>
                    <InputLabel id="trail-system-state-select-label">State</InputLabel>
                    <Select
                      labelId="trail-system-state-select-label"
                      id="trail-system-state-select"
                      value={tempTrailSystem.state || ''}
                      label="State"
                      onChange={(e) => {
                        setTempTrailSystem((prev) => ({ ...prev, state: e.target.value as USStates }))
                      }}
                    >
                      {Object.entries(USStates).map((eachState) => {
                        return (
                          <MenuItem key={eachState[1]} value={eachState[1]}>
                            {startCase(camelCase(eachState[0]))}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </FormControl>
                </Stack>

                <Divider />
                <SectionSubheading
                  title="Parking Lots"
                  description="Trails in this system can pick one of these instead of entering their own coordinates."
                />
                {tempTrailSystem.parkingLots?.map((parkingLot, index) => (
                  <Box key={index} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="subtitle1">{parkingLot.name || `Parking Lot ${index + 1}`}</Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Fetch coordinates from Google Maps">
                          <IconButton
                            aria-label={`Fetch coordinates for parking lot ${index + 1} from Google Maps`}
                            onClick={() => setParkingDialogIndex(index)}
                          >
                            <TravelExploreIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove parking lot">
                          <IconButton
                            aria-label={`Remove parking lot ${index + 1}`}
                            color="error"
                            onClick={() => removeParkingLot(index)}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Stack spacing={2}>
                      <DetailInput<ParkingLot>
                        label="Name"
                        field="name"
                        type="text"
                        required
                        placeholder={index === 0 ? 'e.g. Main Parking' : 'e.g. North Lot'}
                        tempDetail={parkingLot}
                        onChange={(value) => updateParkingLot(index, 'name', String(value))}
                        keyDownSubmit={keyDownSubmit}
                      />
                      <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2}>
                        <DetailInput<ParkingLot>
                          label="GPS Latitude"
                          field="gpsLat"
                          type="number"
                          placeholder="e.g. 42.3601"
                          tempDetail={parkingLot}
                          onChange={(value) => updateParkingLot(index, 'gpsLat', Number(value))}
                          keyDownSubmit={keyDownSubmit}
                        />
                        <DetailInput<ParkingLot>
                          label="GPS Longitude"
                          field="gpsLong"
                          type="number"
                          placeholder="e.g. -71.0589"
                          tempDetail={parkingLot}
                          onChange={(value) => updateParkingLot(index, 'gpsLong', Number(value))}
                          keyDownSubmit={keyDownSubmit}
                        />
                      </Stack>
                    </Stack>
                  </Box>
                ))}
                <Button variant="outlined" onClick={addParkingLot} fullWidth>
                  + Add Parking Lot
                </Button>
              </FormSection>

              {/* --- Weather & Algorithm ---------------------------------------- */}
              <FormSection id={TrailSystemSectionIds.weather}>
                {hasViewWeatherGroupPermissions && weatherGroupsResponse?.data && (
                  <>
                    <SectionSubheading
                      title="Weather Sources"
                      description="Drag to reorder. The status algorithm uses the highest priority group that has data."
                    />
                    <WeatherGroupPriorityList
                      items={tempTrailSystem.weatherGroupsWithPriority ?? []}
                      weatherGroups={weatherGroupsResponse.data}
                      onChange={(idx, weatherGroupId) =>
                        handleWeatherGroupChange(idx, 'weatherGroupId', weatherGroupId)
                      }
                      onRemove={handleRemoveWeatherGroup}
                      onAdd={handleAddWeatherGroup}
                      onReorder={handleReorderWeatherGroups}
                    />
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="trail-system-display-weather-group-select-label">
                        Display Weather Group
                      </InputLabel>
                      <Select
                        labelId="trail-system-display-weather-group-select-label"
                        id="trail-system-display-weather-group-select"
                        value={tempTrailSystem.displayWeatherGroupId || ''}
                        label="Display Weather Group"
                        onChange={(e) => {
                          setTempTrailSystem((prev) => ({ ...prev, displayWeatherGroupId: Number(e.target.value) }))
                        }}
                      >
                        <MenuItem value="">-Select-</MenuItem>
                        {weatherGroupsResponse.data.map((eachWeatherGroup) => {
                          return (
                            <MenuItem key={eachWeatherGroup.id} value={eachWeatherGroup.id}>
                              {eachWeatherGroup.name}
                            </MenuItem>
                          )
                        })}
                      </Select>
                      <FormHelperText>The weather shown to riders in the app.</FormHelperText>
                    </FormControl>
                    <Divider />
                  </>
                )}
                <SectionSubheading
                  title="Algorithm Tuning"
                  description="Applies to every trail in this system unless the trail sets its own value."
                />
                <DetailInput<TrailSystem>
                  label="Precipitation Lookback Days"
                  field="precipitationLookbackDays"
                  type="number"
                  helperText="Used by the Status algorithm to look back at weather records for X days. This value is only used if a child trail has no lookback defined. If not set here, the region's value will be used instead. If the region has no value, defaults to 7 days. Must be a non-negative number (e.g. 0,3, etc.)."
                  tempDetail={tempTrailSystem}
                  setTempDetail={setTempTrailSystem}
                  keyDownSubmit={keyDownSubmit}
                />
                <Stack direction={{ zero: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'flex-start' }}>
                  <DetailInput<TrailSystem>
                    label="Dry-Time Modifier"
                    field="dryTimeModifier"
                    type="number"
                    helperText="A larger number makes a trail move back to low impact quicker (e.g. 0.5 for 50% or 1.5 for 150%, default 1). Leave blank to use the default."
                    tempDetail={tempTrailSystem}
                    setTempDetail={setTempTrailSystem}
                    keyDownSubmit={keyDownSubmit}
                  />
                  <DetailInput<TrailSystem>
                    label="Saturation Modifier"
                    field="saturationModifier"
                    type="number"
                    helperText="How strongly a wet lookback window raises a trail's decayed-precipitation reading (e.g. 0.5 for 50% or 1.5 for 150%, default 1; 0 ignores prior-week wetness). Leave blank to use the default."
                    tempDetail={tempTrailSystem}
                    setTempDetail={setTempTrailSystem}
                    keyDownSubmit={keyDownSubmit}
                  />
                </Stack>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="trail-system-freeze-thaw-enabled-select-label">Freeze/Thaw Enabled</InputLabel>
                  <Select
                    labelId="trail-system-freeze-thaw-enabled-select-label"
                    id="trail-system-freeze-thaw-enabled-select"
                    value={
                      tempTrailSystem.freezeThawEnabled === null || tempTrailSystem.freezeThawEnabled === undefined
                        ? 'inherit'
                        : tempTrailSystem.freezeThawEnabled
                        ? 'true'
                        : 'false'
                    }
                    label="Freeze/Thaw Enabled"
                    onChange={(e) => {
                      setTempTrailSystem((prev) => ({
                        ...prev,
                        freezeThawEnabled:
                          e.target.value === 'inherit' ? null : e.target.value === 'true' ? true : false,
                      }))
                    }}
                  >
                    <MenuItem value="inherit">Inherit (from Region)</MenuItem>
                    <MenuItem value="true">Enabled</MenuItem>
                    <MenuItem value="false">Disabled</MenuItem>
                  </Select>
                  <FormHelperText>
                    Enable or disable Freeze/Thaw algorithm for this trail system. If set to Inherit, will use Region
                    setting.
                  </FormHelperText>
                </FormControl>
              </FormSection>

              {/* --- Trail Stewards --------------------------------------------- */}
              <FormSection id={TrailSystemSectionIds.stewards}>
                <FormControl fullWidth>
                  <InputLabel id="mutiple-select-label">Trail Stewards</InputLabel>
                  <Select
                    labelId="mutiple-select-label"
                    label="Trail Stewards"
                    multiple
                    value={tempTrailSystem.trailStewards || []}
                    onChange={handleChange}
                    renderValue={(selected) => (
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {trailStewardResponse?.data
                          .filter((eachTrailSteward) => selected.includes(eachTrailSteward.id))
                          .map((eachTrailSteward) => (
                            <Chip key={eachTrailSteward.id} size="small" label={eachTrailSteward.name} />
                          ))}
                      </Stack>
                    )}
                  >
                    <MenuItem value={-2}>
                      <ListItemIcon>
                        <Checkbox
                          checked={isAllSelected}
                          indeterminate={
                            trailStewardResponse !== undefined &&
                            tempTrailSystem.trailStewards !== undefined &&
                            tempTrailSystem.trailStewards?.length > 0 &&
                            tempTrailSystem.trailStewards?.length < trailStewardResponse?.data.length
                          }
                        />
                      </ListItemIcon>
                      <ListItemText
                        style={{
                          fontWeight: 500,
                        }}
                        primary="Select All"
                      />
                    </MenuItem>
                    {trailStewardResponse?.data.map((trailSteward) => (
                      <MenuItem key={trailSteward.id} value={trailSteward.id}>
                        <ListItemIcon>
                          <Checkbox checked={tempTrailSystem.trailStewards?.includes(trailSteward.id)} />
                        </ListItemIcon>
                        <ListItemText primary={trailSteward.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FormSection>

              {/* --- Integrations section (Trailforks-Pusher-Spec.md) ----------- */}
              <FormSection id={TrailSystemSectionIds.integrations}>
                <SectionSubheading title="Trailforks" />
                <TextField
                  id="trail-system-trailforks-region-id"
                  label="Trailforks Region ID(s)"
                  variant="outlined"
                  fullWidth
                  value={tempTrailSystem.trailforksRegionId ?? ''}
                  onChange={(e) =>
                    setTempTrailSystem((prev) => ({
                      ...prev,
                      trailforksRegionId: e.target.value,
                    }))
                  }
                  onKeyDown={keyDownSubmit}
                  error={!!invalidTrailforksRegionPart}
                  helperText={
                    invalidTrailforksRegionPart
                      ? `"${invalidTrailforksRegionPart}" is not a valid region id. Use one or more positive integers separated by commas.`
                      : 'Numeric Trailforks region id (e.g. 3539 for Brown County). Comma-separate ids if this system spans multiple TF regions. Required to enable Trailforks Status Sync.'
                  }
                />
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="trail-system-trailforks-sync-enabled-select-label">Trailforks Status Sync</InputLabel>
                  <Select
                    labelId="trail-system-trailforks-sync-enabled-select-label"
                    id="trail-system-trailforks-sync-enabled-select"
                    value={tempTrailSystem.trailforksSyncEnabled ? 'true' : 'false'}
                    label="Trailforks Status Sync"
                    disabled={!canEnableTrailforksSync}
                    onChange={(e) => {
                      setTempTrailSystem((prev) => ({
                        ...prev,
                        trailforksSyncEnabled: e.target.value === 'true',
                      }))
                    }}
                  >
                    <MenuItem value="false">Disabled</MenuItem>
                    <MenuItem value="true">Enabled</MenuItem>
                  </Select>
                  <FormHelperText>
                    {canEnableTrailforksSync
                      ? "Push this system's trail statuses to Trailforks hourly. Individual trails can override this in either direction. Requires each trail to have at least one Trailforks Trail ID mapped."
                      : 'Enter a valid Trailforks Region ID above before enabling sync.'}
                  </FormHelperText>
                </FormControl>
              </FormSection>
            </SectionedForm>
            <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Stack>
          </>
        )}
      {/* Google Maps Dialog */}
      <GoogleMapsFetchDialog
        open={isGoogleMapsDialogOpen}
        onClose={() => setIsGoogleMapsDialogOpen(false)}
        dialogTitle="Fetch Address Details from Google Maps URL"
        onFetch={(data) => {
          setTempTrailSystem((prev) => ({
            ...prev,
            street: data.street !== undefined ? data.street : prev.street,
            city: data.city !== undefined ? data.city : prev.city,
            state: data.state !== undefined ? (data.state as USStates) : prev.state,
          }))
          const populated = [
            data.street !== undefined ? `street: ${data.street}` : null,
            data.city !== undefined ? `city: ${data.city}` : null,
            data.state !== undefined ? `state: ${data.state}` : null,
          ].filter(Boolean)
          if (populated.length > 0) {
            SnackbarContext.show(`Address fetched (${populated.join(', ')})`)
          } else {
            SnackbarContext.show('No address details found in that link', 'error')
          }
        }}
      />
      <GoogleMapsFetchDialog
        open={parkingDialogIndex !== null}
        onClose={() => setParkingDialogIndex(null)}
        dialogTitle="Fetch Parking Details from Google Maps URL"
        onFetch={(data) => {
          if (parkingDialogIndex !== null) {
            setTempTrailSystem((prev) => ({
              ...prev,
              parkingLots:
                prev.parkingLots?.map((lot, index) =>
                  index === parkingDialogIndex
                    ? {
                        ...lot,
                        gpsLat: data.latitude !== undefined ? Number(data.latitude) : lot.gpsLat,
                        gpsLong: data.longitude !== undefined ? Number(data.longitude) : lot.gpsLong,
                      }
                    : lot,
                ) || [],
            }))
          }
          setParkingDialogIndex(null)
          if (data.latitude !== undefined && data.longitude !== undefined) {
            SnackbarContext.show(`Coordinates fetched (${data.latitude}, ${data.longitude})`)
          } else {
            SnackbarContext.show('No coordinates found in that link', 'error')
          }
        }}
      />
    </>
  )
}
export default TrailSystemForm
